import { useForm } from 'react-hook-form';
import { Dialog } from 'primereact/dialog';
import MailCreateForm from './MailCreateForm';
import useSendMail from '../hooks/useSendMail';
import { UserDetailsContext, useUserDetailsContext } from 'context/userDetailsContext';
import { useLoaderDispatch } from 'context/LoaderContext';
// import { uploadFiles, UPLOAD_CATEGORY } from 'services/uploadService';
import { useContext, useState, useEffect } from 'react';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { messagesPrepare } from 'services/mailbox/mailboxService';
import { uploadFilesV2, UPLOAD_CATEGORY } from 'utils/filesUtils';
import { uuidv4 } from 'common/uuidGenerator';
import { listGroupedFiles } from 'services/fileManager/fileManagerServices';

const mapCaseFilesToOptions = (arr) =>
  arr.map((item) => ({
    ...item,
    label: item.display_name,
    value: {
      file_path: item.file_path,
      size: item.size,
      display_name: item.display_name,
      file_name: item.file_name,
      file_type: item.file_type,
      created_by: item.created_by_details,
      is_new_file: true,
      sk: item.sk,
      pk: item.pk,
    },
  }));

const MailCreateModal = ({ show, setShow, caseId, caseName, isLead, handleSentEmailList, existingFiles: existingFilesFromCase = [] }) => {
  const {
    handleSubmit,
    control,
    resetField,
    setError,
    formState: { errors },
    reset,
    watch,
    trigger,
  } = useForm();

  const { sendMail, loading: sendingMail } = useSendMail();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const { userDetails } = useUserDetailsContext();
  const [pendingUploadFiles, setPendingUploadFiles] = useState([]);
  const [fetchedExistingFiles, setFetchedExistingFiles] = useState([]);
  const selectedCase = watch('case');

  const existingFilesForPicker = caseId ? existingFilesFromCase : fetchedExistingFiles;

  useEffect(() => {
    if (caseId) {
      setFetchedExistingFiles([]);
      return;
    }
    const cid = selectedCase?.id;
    if (!cid) {
      setFetchedExistingFiles([]);
      return;
    }
    let cancelled = false;
    listGroupedFiles({ case_id: cid, all_files: true })
      .then((res) => {
        if (cancelled) return;
        const arr = Array.isArray(res?.data) ? res.data : [];
        setFetchedExistingFiles(mapCaseFilesToOptions(arr));
      })
      .catch(() => {
        if (!cancelled) setFetchedExistingFiles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [caseId, selectedCase?.id]);

  const handleClose = () => {
    reset({ recipient_list: [], subject: '', html_body: '', cc_list: [], bcc_list: [], reply_to: [], case: null, attachments: [] });
    setPendingUploadFiles([]);
    setShow(false);
  };
  const setLoader = useLoaderDispatch();
  const userContext = useContext(UserDetailsContext);
  const userId = userContext?.firmDetails?.current_user;

  const onSubmit = async (data) => {
    const selectedExisting = (data?.attachments || []).filter((a) => a && typeof a === 'object' && a.file_path);
    const maxFiles = 20;
    if (selectedExisting.length + pendingUploadFiles.length > maxFiles) {
      addToast(false, toastConstant.toasterType.WARN, toastConstant.header.WARNINIG, toastConstant.message.FILE_UPLOAD_LIMIT_EXCEEDED);
      return;
    }

    let uploadedMeta = [];
    if (pendingUploadFiles.length > 0) {
      try {
        setLoader(true);
        setIsLoading(true);

        const options = {
          module: 'email/outbound',
          caseId: caseId ? caseId : data?.case?.id,
          userId: userId,
          category: UPLOAD_CATEGORY.EMAILS,
          isTemp: true,
          tempPathId: uuidv4(),
        };
        // const res = await uploadFiles(data?.attachments, 'email/outbound', caseId ? caseId : data?.case?.id, userId);
        // const res = await uploadFiles(data?.attachments, 'email/outbound', caseId ? caseId : data?.case?.id, userId, {
        //   category: UPLOAD_CATEGORY.EMAILS,
        //   email_message_id : emailMessageData?.message_id || null,
        // });
        uploadedMeta = await uploadFilesV2(pendingUploadFiles, options);
        setLoader(false);
        setIsLoading(false);
      } catch (err) {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, err.message);
        setLoader(false);
        setIsLoading(false);
        console.log(err);
        return;
      }
    }

    data.attachments = [...selectedExisting, ...uploadedMeta];

    const formattedData = {
      // recipient_list: data.recipient_list.split(',').map((email) => email.trim()),
      recipient_list: data?.recipient_list,
      subject: data.subject,
      email_content: data.html_body,
      // cc_list: data.cc_list ? data.cc_list.split(',').map((email) => email.trim()) : [],
      cc_list: data?.cc_list ? data?.cc_list : [],
      bcc_list: data?.bcc_list ? data?.bcc_list : [],
      reply_to: data.reply_to || undefined,
      case_id: caseId ? caseId : data?.case?.id,
      case_name: caseName ? caseName : data?.case?.case_name,
      is_lead: isLead !== null ? isLead : data?.case?.is_lead,
      attachments: data?.attachments,
    };
    try {
      setLoader(true);
      setIsLoading(true);
      const response = await sendMail(formattedData);
      setLoader(false);
      setIsLoading(false);
      if (response.status === 200) {
        const formattedDataForSentList = {
          pk: response?.data?.pk,
          sk: response?.data?.sk,
          subject: data.subject,
          is_sent: true,
          email_content: data.html_body,
          email_from: {
            user_email: userDetails?.business_email,
            user_name: userDetails?.first_name + ' ' + userDetails?.last_name,
          },
          time: new Date().toISOString(),
        };
        handleClose();
        reset();
        handleSentEmailList?.(formattedDataForSentList);
      }
    } catch (error) {
      console.error(error);
      setLoader(false);
      setIsLoading(false);
    }
  };

  const headerComponent = (data) => {
    return (
      <div>
        <i className="icon-window-filled modal-close-icon F-size14 pointer" onClick={data?.onHide}></i>
        {/* <i className="icon-window-filled F-size14 icon-minimize"></i> */}
      </div>
    );
  };
  return (
    <Dialog
      header={headerComponent}
      visible={show}
      onHide={handleClose}
      className="create-msg-popup"
      closable={false}
      style={{ width: '50vw' }}
    >
      <MailCreateForm
        show={show}
        onSubmit={onSubmit}
        handleSubmit={handleSubmit}
        handleClose={handleClose}
        caseId={caseId}
        control={control}
        errors={errors}
        resetField={resetField}
        setError={setError}
        watch={watch}
        trigger={trigger}
        isLoading={isLoading}
        existingFiles={existingFilesForPicker}
        pendingUploadFiles={pendingUploadFiles}
        setPendingUploadFiles={setPendingUploadFiles}
      />
      {sendingMail && <p className="text-center mt-3">Sending mail...</p>}
    </Dialog>
  );
};

export default MailCreateModal;
